import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { getUserSearchFilterDefaults } from 'src/hooks/useInitUserSearchFilter';
import { KSK2101_R01Dto } from './Dto_KSK2101_R01';
import { getCurrentMonthDateRange, getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';

export interface KSK2101_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	workplaceCd: string;
	jobTeam: string;
	closeTag: string;
	workEmp: string;
	workEmpNm: string;
	workCloseShow: string;
}

// (소계 제거로 확장 타입 불필요)

/**
 * Zustand 스토어의 상태 및 액션 정의
 */
export interface Store_KSK2101_R01 {
    KSK2101_R01Filter: KSK2101_R01Filter;
    KSK2101_R01PastFilter: KSK2101_R01Filter;
    setKSK2101_R01Filter: (filter: Partial<KSK2101_R01Filter>) => void;
    setKSK2101_R01PastFilter: (filter: Partial<KSK2101_R01Filter>) => void;

    // === 리스트 관련 ===
    KSK2101_R01List: KSK2101_R01Dto[];
    setKSK2101_R01List: (KSK2101_R01List: KSK2101_R01Dto[]) => void;
    insertKSK2101_R01List: (data: KSK2101_R01Dto[], index?: number) => void;
    removeKSK2101_R01List: (index: number) => void;
    updateKSK2101_R01List: (data: KSK2101_R01Dto, index: number) => void;

    // === 선택 관련 ===
    selectedKSK2101_R01List: string[];
    setSelectedKSK2101_R01List: (idList: string[]) => void;
    addSelectedKSK2101_R01List: (idList: string[]) => void;
    removeSelectedKSK2101_R01List: (idList?: string[]) => void;
    initSelectedKSK2101_R01List: () => void;

    // === AG Grid 컬럼 상태 관리 ===
    columnState: ColumnState[] | null;
    setColumnState: (state: ColumnState[] | null) => void;

    allCount: number;
    setAllCount: (allCount: number) => void;

    // (소계 제거) 추가 상태 없음

    // === 전체 초기화 ===
    initData: () => void;
}

/**
 * 기본 날짜 범위 (현재 월)
 */
const defaultDateRange: DateRange = getCurrentMonthDateRange();

export const createDefaultKSK2101_R01Filter = (): KSK2101_R01Filter => ({
	...getUserSearchFilterDefaults(),
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	jobTeam: '',
	closeTag: 'N',
	workEmp: '',
	workEmpNm: '',
	workCloseShow: 'N',
});

export const defaultKSK2101_R01Filter: KSK2101_R01Filter = createDefaultKSK2101_R01Filter();

/**
 * Zustand 스토어 정의
 */
export const useKSK2101_R01Store = create<Store_KSK2101_R01>((set) => {
    /**
     * 공통 필터 설정 함수 생성
     */
    const createFilterSetter = (filterKey: keyof Pick<Store_KSK2101_R01, 'KSK2101_R01Filter' | 'KSK2101_R01PastFilter'>) => 
        (filter: Partial<KSK2101_R01Filter>) => set((state) => ({
            ...state,
            [filterKey]: { ...state[filterKey], ...filter }
        }));

    /** 
     * 선택된 항목 제거 헬퍼 함수
     */
    const removeItemsFromArray = <T>(array: T[], itemsToRemove: T[]): T[] => {
        return array.filter(item => !itemsToRemove.includes(item));
    };

    return {
        KSK2101_R01Filter: defaultKSK2101_R01Filter,
        KSK2101_R01PastFilter: defaultKSK2101_R01Filter,
        setKSK2101_R01Filter: createFilterSetter('KSK2101_R01Filter'),
        setKSK2101_R01PastFilter: createFilterSetter('KSK2101_R01PastFilter'),

        // === 리스트 관련 ===
        KSK2101_R01List: [],
        setKSK2101_R01List: (KSK2101_R01List: KSK2101_R01Dto[]) => set({ KSK2101_R01List }),
        insertKSK2101_R01List: (data: KSK2101_R01Dto[], index?: number) => set((state) => {
            const _KSK2101_R01List = [...state.KSK2101_R01List];
            
            if (index !== undefined) {
                _KSK2101_R01List.splice(index, 0, ...data);
            } else {
                _KSK2101_R01List.push(...data);
            }
            return { ...state, KSK2101_R01List: _KSK2101_R01List };
        }),
        removeKSK2101_R01List: (deletedIndex: number) => set((state) => ({
            ...state,
            KSK2101_R01List: state.KSK2101_R01List.filter((_, index) => index !== deletedIndex),
        })),
        updateKSK2101_R01List: (data: KSK2101_R01Dto, index: number) => set((state) => {
            const _KSK2101_R01List = [...state.KSK2101_R01List];
            _KSK2101_R01List[index] = data;
            return { ...state, KSK2101_R01List: _KSK2101_R01List };
        })
        ,

        // === 선택 관련 ===
        selectedKSK2101_R01List: [],
        setSelectedKSK2101_R01List: (idList: string[]) => set({ selectedKSK2101_R01List: idList }),
        addSelectedKSK2101_R01List: (idList: string[]) => set((state) => {
            const _selectedKSK2101_R01List = [...state.selectedKSK2101_R01List];
            _selectedKSK2101_R01List.push(...idList.filter(value => !_selectedKSK2101_R01List.includes(value)));
            return { ...state, selectedKSK2101_R01List: _selectedKSK2101_R01List };
        }),
        removeSelectedKSK2101_R01List: (idList?: string[]) => set((state) => {
            if (!idList) {
                return { ...state, selectedKSK2101_R01List: [] };
            }
            
            return {
                ...state,
                selectedKSK2101_R01List: removeItemsFromArray(state.selectedKSK2101_R01List, idList),
            };
        }),
        initSelectedKSK2101_R01List: () => set({ selectedKSK2101_R01List: [] }),
        
        // === AG Grid 컬럼 상태 관리 ===
        columnState: null,
        setColumnState: (state) => set({ columnState: state }),

        allCount: 0,
		
        setAllCount: (allCount: number) => set({ allCount }),

        // === 통합 소계 관련 ===
        

        // === 전체 초기화 ===
        initData: () => set(() => ({
            KSK2101_R01Filter: createDefaultKSK2101_R01Filter(),
            KSK2101_R01PastFilter: createDefaultKSK2101_R01Filter(),
            KSK2101_R01List: [],
            selectedKSK2101_R01List: [],
            columnState: null,
            allCount: 0,
		
            
        })),
    };
});


